import { Injectable } from '@angular/core'
import { distinctUntilChanged, EMPTY, filter, fromEvent, map, merge, mergeMap, Observable, of, shareReplay } from 'rxjs'
import { environment } from '../../../environments/environment'
import { AppConfig, AppConfigService } from './app-config.service'
import { AuthService } from './auth.service'

// wrap feature flag in an object so that we can use them directly in *ngIf="(myFeatureFlag$|async) as myFeatureFlag" and add additional data if needed
type FeatureFlag = {
  isEnabled: boolean
}

const SUPPORTED_FEATURE_NAMES = [ 'auth', 'readings', 'reports', 'watches', 'waste', 'customizableCharts', 'periodBasedTimeNavigation', 'versionlessReadingForms', 'editableDevicesOperationDate', 'newAuthFrontend', 'remoteAccess' ] as const

type SupportedFeatureName = (typeof SUPPORTED_FEATURE_NAMES)[number]

const isSupportedFeatureName = (candidate:string): candidate is SupportedFeatureName =>
  (SUPPORTED_FEATURE_NAMES as ReadonlyArray<string>).includes(candidate)

type FeatureFagRequestFromConsole = {
  name: string,
  enable: boolean,
  secret: string
}
type ValidFeatureFagRequestFromConsole = {
  name: SupportedFeatureName,
  enable: boolean,
}

const utf8StringToBuffer = (input:string) => (new TextEncoder()).encode(input)

const bufferToHex = (input:ArrayBuffer):string => Array.from((new Uint8Array(input)))
  .map((byte):string=>byte.toString(16))
  .join('')

@Injectable({ providedIn: 'root' })

export class FeatureFlagsService {

  private validSetFeatureFlagRequestFromConsole$:Observable<ValidFeatureFagRequestFromConsole>
    = fromEvent<CustomEvent<FeatureFagRequestFromConsole>>(window, 'set-feature-flag-request').pipe(
      mergeMap(({ detail:{ name, secret, enable } })=>
        isSupportedFeatureName(name) ? of(secret).pipe(
          map(utf8StringToBuffer),
          mergeMap((encodedSecret)=>crypto.subtle.digest('SHA-256', encodedSecret)),
          map(bufferToHex),
          mergeMap((hash)=>(hash === environment.featureFlagBackdoorSecretHash)
            ? of({ name, enable })
            : EMPTY)
        ) : EMPTY)
    )

  readings$
    = this.featureFlag$('readings', ({ readings: { enableForAll = true } = {} })=>
      (enableForAll === true))

  reports$
    = this.featureFlag$('reports', ({ reports: { enable = false, enableForSpecificUsers = [] } = {} }, { authService })=>
      (enable === true) || (enableForSpecificUsers.includes(authService.getUsername())))

  watches$
    = this.featureFlag$('watches', ({ watches: { enableForAll = false, enableForSpecificUsers = [] } = {} }, { authService })=>
      (enableForAll === true) || (enableForSpecificUsers.includes(authService.getUsername())))

  waste$
    = this.featureFlag$('waste', ({ waste: { enableForAll = false, enableForSpecificUsers = [] } = {} }, { authService })=>
      (enableForAll === true) || (enableForSpecificUsers.includes(authService.getUsername())))
  wasteTimeIntervalCollectionType$ = this.featureFlag$('waste', ({ waste: { enableTimeIntervalCollectionType = true } = {} }, _) => enableTimeIntervalCollectionType)
  wasteFoodWasteCommitment$ = this.featureFlag$('waste', ({ waste: { enableFoodWasteCommitment = false } = {} }, _) => enableFoodWasteCommitment)

  customizableCharts$
    = this.featureFlag$('customizableCharts', (appconfig)=>appconfig.charts.enableCustomization)
  periodBasedTimeNavigation$
    = this.featureFlag$('periodBasedTimeNavigation', (appconfig, { authService })=>{
      const enableForList = appconfig.charts?.enablePeriodBasedTimeNavigationFor
      return (enableForList === undefined)
        ? false
        : enableForList.includes('*') || enableForList.includes(authService.getUsername())
    })

  versionLessReadingForms$
    = this.featureFlag$('versionlessReadingForms', ()=>true)
  editableDevicesOperationDate$
    = this.featureFlag$('editableDevicesOperationDate', ()=>false)

  newAuthFrontend$
    = this.featureFlag$('newAuthFrontend', (appconfig)=>appconfig.auth2.enableNewAuthFrontend ?? false)

  remoteAccess$
    = this.featureFlag$('remoteAccess', ({ remoteAccess: { enableForAll = false, enableForSpecificUsers = [] } = {} }, { authService }) => (enableForAll === true) || (enableForSpecificUsers.includes(authService.getUsername())))

  constructor (
    private appConfigService: AppConfigService,
    private authService: AuthService
  ) {}

  private featureFlag$<FeatureName extends SupportedFeatureName> (
    featureName:FeatureName,
    appConfigToFlag:(appConfig:AppConfig, context: {authService: AuthService})=>boolean,
  ): Observable<FeatureFlag> {
    const isEnabled$ = environment.features[featureName]
      ? merge(
        this.appConfigService.state$.pipe(map((appConfig)=>appConfigToFlag(appConfig, { authService: this.authService }))),
        this.validSetFeatureFlagRequestFromConsole$.pipe(
          filter(({ name })=>name === featureName),
          map(({ enable })=>enable)
        )
      )
      : of(false)
    return isEnabled$.pipe(
      distinctUntilChanged(),
      map((isEnabled)=>({ isEnabled })),
      shareReplay(1)
    )
  }
}
